import React, { useState, useMemo } from "react";
import {
  ToolShell, Field, UnitNumber, Select, Result,
  hazenWilliamsHeadLoss, pipeVelocity, toBase, fromBase, altsFor, fmt, UNITS,
  FittingsPicker, fittingsEquivalentLength, fittingsSummary,
  SpecCard, SpecValue,
} from "./_shared";

// Hazen-Williams C-factor — industry-standard values.
const MATERIALS = [
  { value: "150",  label: "HDPE / Polyethylene · C = 150" },
  { value: "150b", label: "PVC · C = 150" },
  { value: "140",  label: "Copper · C = 140" },
  { value: "130",  label: "Cast iron (cement-lined) · C = 130" },
  { value: "120",  label: "Galvanised steel · C = 120" },
  { value: "100",  label: "Steel (older / mild scale) · C = 100" },
  { value: "100c", label: "Concrete · C = 100" },
];

export default function FrictionLossCalculator() {
  const [material, setMaterial] = useState("150");

  const [idVal, setIdVal] = useState(50);
  const [idUnit, setIdUnit] = useState("mm");

  const [lenVal, setLenVal] = useState(100);
  const [lenUnit, setLenUnit] = useState("m");

  const [flowVal, setFlowVal] = useState(2);
  const [flowUnit, setFlowUnit] = useState("Lps");

  const [fittings, setFittings] = useState({});

  const C = parseFloat(material);
  // convert each input to its SI base
  const id_m     = toBase("length_small", idVal,  idUnit)  / 1000;          // mm→m
  const length_m = toBase("length_big",   lenVal, lenUnit);                  // already m
  const eqlen_m  = fittingsEquivalentLength(fittings, id_m);
  const total_length_m = length_m + eqlen_m;
  const flow_m3s = toBase("flow",         flowVal, flowUnit);                // m³/s

  const hL_m   = useMemo(() => hazenWilliamsHeadLoss({ flow_m3s, id_m, length_m: total_length_m, C }), [flow_m3s, id_m, total_length_m, C]);
  const v_mps  = useMemo(() => pipeVelocity({ flow_m3s, id_m }), [flow_m3s, id_m]);
  const dP_bar = hL_m !== null ? hL_m * 0.0980665 : null;     // 1 m H₂O = 0.0980665 bar
  const lossPer100m = hL_m !== null && total_length_m ? (hL_m / total_length_m) * 100 : null;

  // velocity status band
  const vStatus = !v_mps ? undefined
                : v_mps > 3.0 ? "alert"
                : v_mps > 2.5 ? "warn"
                : v_mps < 0.6 ? "warn"
                : "ok";
  const vNote   = !v_mps ? null
                : v_mps > 3.0 ? "Above 3.0 m/s · erosive and noisy. Up-size the pipe."
                : v_mps > 2.5 ? "Above 2.5 m/s · acceptable for short runs only."
                : v_mps < 0.6 ? "Below 0.6 m/s · risk of sedimentation in horizontal runs."
                :               "Within the recommended 0.6–2.5 m/s range.";

  return (
    <ToolShell
      slug="hazen-williams"
      title="Hazen-Williams friction loss"
      oneLiner="Head loss along a pipe run. Useful for sizing borehole risers, pressure mains and irrigation manifolds."
      onExport={() => ({
        inputs: [
          { label: "Pipe material", value: MATERIALS.find((m) => m.value === material)?.label ?? material },
          { label: "Internal diameter", value: idVal, unit: UNITS.length_small.labels[idUnit] },
          { label: "Pipe length", value: lenVal, unit: UNITS.length_big.labels[lenUnit] },
          { label: "Equivalent length (fittings)", value: fmt(eqlen_m, 2), unit: "m" },
          ...fittingsSummary(fittings).map((f) => ({ label: `· ${f.label}`, value: `× ${f.qty}` })),
          { label: "Flow rate", value: flowVal, unit: UNITS.flow.labels[flowUnit] },
        ],
        results: [
          {
            label: "Head loss",
            value: fmt(hL_m), unit: "m H₂O",
            alts: hL_m !== null ? [{ value: fmt(fromBase("length_big", hL_m, "ft")), unit: "ft H₂O" }] : null,
            note: lossPer100m !== null ? `≈ ${fmt(lossPer100m)} m per 100 m of pipe (over ${fmt(total_length_m, 1)} m total)` : null,
          },
          {
            label: "Pressure loss",
            value: fmt(dP_bar), unit: "bar",
            alts: dP_bar !== null ? altsFor("pressure", dP_bar, ["kPa", "psi"], 1) : null,
          },
          {
            label: "Velocity",
            value: fmt(v_mps), unit: "m/s",
            alts: v_mps !== null ? [{ value: fmt(fromBase("velocity", v_mps, "fps")), unit: "ft/s" }] : null,
            status: vStatus,
            note: vNote,
          },
        ],
        notes: ["Formula: Hazen-Williams hL = 10.67 × L × Q^1.852 / ( C^1.852 × D^4.87 )"],
        spec: hL_m !== null ? {
          title: "Friction-loss specification",
          body: `A run of <strong>${fmt(lenVal, 1)} ${UNITS.length_big.labels[lenUnit]}</strong> of <strong>${fmt(idVal, 1)} ${UNITS.length_small.labels[idUnit]} ID</strong> ${MATERIALS.find((m) => m.value === material)?.label ?? "pipe"} at <strong>${fmt(flowVal, 2)} ${UNITS.flow.labels[flowUnit]}</strong> loses <strong>${fmt(hL_m, 2)} m H₂O</strong> (<strong>${fmt(dP_bar, 2)} bar</strong>) to friction, at a pipe velocity of <strong>${fmt(v_mps, 2)} m/s</strong>.`,
        } : null,
      })}
    >
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Pipe material" span={2}>
          <Select value={material} onChange={setMaterial} options={MATERIALS} testid="hw-material" />
        </Field>
        <Field label="Internal diameter">
          <UnitNumber dim="length_small" value={idVal} onChange={setIdVal} unit={idUnit} onUnitChange={setIdUnit}
            units={["mm", "cm", "m", "inch"]} testid="hw-id" />
        </Field>
        <Field label="Pipe length">
          <UnitNumber dim="length_big" value={lenVal} onChange={setLenVal} unit={lenUnit} onUnitChange={setLenUnit}
            units={["m", "ft", "km"]} testid="hw-length" />
        </Field>
        <Field label="Flow rate" span={2}>
          <UnitNumber dim="flow" value={flowVal} onChange={setFlowVal} unit={flowUnit} onUnitChange={setFlowUnit}
            units={["Lps", "Lpm", "m3h", "m3d", "gpm"]} testid="hw-flow" />
        </Field>

        {/* Fittings — Crane-TP410 equivalent-length picker */}
        <FittingsPicker qtyMap={fittings} onChange={setFittings} id_m={id_m} testid="hw-fittings" />
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-6">
        <Result
          label="Head loss"
          primary={{ value: fmt(hL_m), unit: "m H₂O" }}
          alts={hL_m !== null ? [
            { value: fmt(fromBase("length_big", hL_m, "ft")), unit: "ft H₂O" },
          ] : null}
          note={lossPer100m !== null ? `≈ ${fmt(lossPer100m)} m per 100 m (over ${fmt(total_length_m, 1)} m total)` : null}
          testid="hw-result-head"
        />
        <Result
          label="Pressure loss"
          primary={{ value: fmt(dP_bar), unit: "bar" }}
          alts={dP_bar !== null ? altsFor("pressure", dP_bar, ["kPa", "psi"], 1) : null}
          testid="hw-result-bar"
        />
        <Result
          label="Velocity"
          primary={{ value: fmt(v_mps), unit: "m/s" }}
          alts={v_mps !== null ? [{ value: fmt(fromBase("velocity", v_mps, "fps")), unit: UNITS.velocity.labels.fps }] : null}
          status={vStatus}
          note={vNote}
          testid="hw-result-vel"
        />
      </div>

      {hL_m !== null && (
        <SpecCard
          eyebrow="Friction-loss specification"
          testid="hw-spec-card"
          footer={vStatus === "alert" || vStatus === "warn"
            ? `Pipe velocity is ${vNote ? vNote.toLowerCase() : "outside the recommended band"} — consider re-sizing.`
            : "Pipe velocity is within the recommended 0.6–2.5 m/s engineering envelope."}
        >
          A run of <SpecValue>{fmt(lenVal, 1)} {UNITS.length_big.labels[lenUnit]}</SpecValue>{" "}
          of <SpecValue>{fmt(idVal, 1)} {UNITS.length_small.labels[idUnit]} ID</SpecValue>{" "}
          {MATERIALS.find((m) => m.value === material)?.label ?? "pipe"} at{" "}
          <SpecValue>{fmt(flowVal, 2)} {UNITS.flow.labels[flowUnit]}</SpecValue> loses{" "}
          <SpecValue>{fmt(hL_m, 2)} m H₂O</SpecValue> ({" "}
          <SpecValue>{fmt(dP_bar, 2)} bar</SpecValue>) to friction, at a pipe velocity of{" "}
          <SpecValue>{fmt(v_mps, 2)} m/s</SpecValue>.
        </SpecCard>
      )}
    </ToolShell>
  );
}
